import { useState } from 'react';
import { Button, CircularProgress } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { captureMessage } from '../../lib/error-handler';
import { EhrFlowsheetRecord, Ride } from '../../generated/graphql';
import { ensureErrorIsJson } from '../../lib/json-error';
import ENV from '../../constants/Env';
import { getToken } from '../../auth/onward-token';
import { useEhrContext } from '../../contexts/ehr-context';
import { useToast } from '../layout/Toast';

const { apiUrl } = ENV;

type EhrWriteRideDetailsProps = {
  ride: Ride;
};

export default function EhrWriteRideDetails({
  ride,
}: EhrWriteRideDetailsProps) {
  const { ehrSession, fhirClient } = useEhrContext();
  const authToken = getToken();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const { ehrData } = ride;
  const isValidRideStatus =
    ride.rideCompletionStatus &&
    ['confirmed', 'in-progress', 'completed'].includes(
      ride.rideCompletionStatus,
    ) === true;

  // If there is no active EHR session, if no EHR data is returned for this
  // ride, or if we aren't in a specific ride status, then we don't want to
  // show the button.
  if (
    !ehrData ||
    !ehrData.encounterId ||
    !ehrData.patientResourceId ||
    !ehrSession ||
    !fhirClient ||
    !isValidRideStatus
  ) {
    return null;
  }

  const logFhirRequest = async (
    patientId: string,
    requestBody: object,
    responseBody: object | string | null,
    isErrorResponse?: boolean,
  ) => {
    if (!authToken) {
      captureMessage('[Epic EHR] - Write - No Onward auth token found');
      return;
    }

    if (!ehrSession) {
      captureMessage('[Epic EHR] - Write - Missing EHR session');
      return;
    }

    const bodyData = {
      serviceName: 'Observation.Create',
      resourceId: patientId,
      searchQuery: requestBody,
      responseBody,
      isErrorResponse: isErrorResponse === true,
    };
    const response = await fetch(
      `${apiUrl}/api/ehr/sessions/${ehrSession.ehrSessionId}/fhir_request`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Access-Token': authToken.accessToken,
          Client: authToken.client,
          Uid: authToken.uid,
        },
        body: JSON.stringify(bodyData),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      captureMessage(
        `[Epic EHR] - Launch FHIR - Error saving Observation.Create FHIR response for session id '${
          ehrSession.ehrSessionId
        }': ${JSON.stringify(data)}`,
      );
    }
  };

  const sendFhirRequest = async (
    flowsheet: EhrFlowsheetRecord,
    patientId?: string | null,
    encounterId?: string | null,
  ) => {
    if (!patientId || !encounterId) {
      return;
    }

    const createParams = {
      resourceType: 'Observation',
      // Pretty sure FHIR requires you to set "final" here.
      status: 'final',
      category: [
        {
          coding: [
            {
              system:
                'http://terminology.hl7.org/CodeSystem/observation-category',
              code: 'vital-signs',
              display: 'Vital Signs',
            },
          ],
          text: 'Vital Signs',
        },
      ],
      code: {
        coding: [
          {
            system:
              'http://open.epic.com/FHIR/StructureDefinition/observation-flowsheet-id',
            code: flowsheet.id,
          },
        ],
      },
      subject: {
        reference: `Patient/${patientId}`,
      },
      encounter: {
        reference: `Encounter/${encounterId}`,
      },
      effectiveDateTime: new Date().toISOString(),
      valueCodeableConcept: {
        coding: [
          {
            code: flowsheet.value,
          },
        ],
      },
    };

    try {
      const response = await fhirClient.create(createParams);
      const contentType = response.headers?.get('content-type');
      const contentLength = response.headers?.get('content-length');
      const hasBody =
        contentType?.includes('application/json') && contentLength !== '0';
      let jsonResponse: object | string | null = null;

      if (hasBody) {
        jsonResponse = await response.json();
      }

      await logFhirRequest(
        patientId,
        createParams,
        jsonResponse,
        response.ok === false,
      );

      return response.ok;
    } catch (error) {
      const isUnauthorized =
        error instanceof Error &&
        (error.message.includes('401') ||
          (error as unknown as { status?: number }).status === 401);

      await logFhirRequest(
        patientId,
        createParams,
        ensureErrorIsJson(error),
        true,
      );

      if (isUnauthorized) {
        throw new Error('EHR_SESSION_EXPIRED');
      }

      return false;
    }
  };

  const handleClick = async () => {
    setLoading(true);

    const records = ehrData.flowsheetRecords ?? [];
    // We need to filter out all the records where there is no value or it is an
    // empty string. Sending those values to FHIR will cause it to error out.
    const filteredRecords = records.filter(
      r => r.value !== undefined && r.value !== null && r.value !== '',
    );
    const concurrencyLimit = 4;
    const results: PromiseSettledResult<boolean | undefined>[] = [];

    // Throttle the FHIR requests so there are no more than 4 going at once.
    for (let i = 0; i < filteredRecords.length; i += concurrencyLimit) {
      const batch = filteredRecords.slice(i, i + concurrencyLimit);
      const batchResults = await Promise.allSettled(
        batch.map(record =>
          sendFhirRequest(
            record,
            ehrData.patientResourceId,
            ehrData.encounterId,
          ),
        ),
      );
      results.push(...batchResults);
    }

    const sessionExpired = results.some(
      r =>
        r.status === 'rejected' &&
        r.reason instanceof Error &&
        r.reason.message === 'EHR_SESSION_EXPIRED',
    );
    const failures = results.filter(
      r => r.status === 'rejected' || r.value === false,
    );

    if (sessionExpired) {
      captureMessage(`[Epic EHR] - Write - Epic session expired for ride`, {
        rideId: ride.id,
        patientId: ehrData.patientResourceId,
        encounterId: ehrData.encounterId,
      });
      toast?.showMessage(
        'Your Epic session has expired. Please re-launch Onward from Epic.',
      );
    } else if (failures.length > 0) {
      captureMessage(`[Epic EHR] - Write - Failed to write data for ride`, {
        rideId: ride.id,
        patientId: ehrData.patientResourceId,
        encounterId: ehrData.encounterId,
      });
      toast?.showMessage('Failed to document ride in medical record.');
    } else {
      toast?.showMessage('Successfully documented ride in medical record.');
    }

    setLoading(false);
  };

  return (
    <>
      <Button
        color="primary"
        variant="outlined"
        disabled={loading}
        onClick={handleClick}
        startIcon={
          loading ? <CircularProgress size={16} /> : <AssignmentIcon />
        }
      >
        Document in Medical Record
      </Button>
    </>
  );
}
